import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import type { PlaylistItem, videoDetails } from '@jwp/ott-common/types/playlist';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { GET_DROPDOWNS, GetDropdownsVariables, GetDropdownsData } from '@jwp/ott-common/src/graphql/query/broadcastLive.queries';
import { useQuery } from '@apollo/client/react';
import { useTheme } from '../../../../../ThemeContext';

import styles from './ShowMoreDetails.module.scss';

export type ShowMoreDetailsData = {
  item?: (PlaylistItem | videoDetails) & Record<string, unknown>;
  primaryMetadata?: string | null;
};

type Props = {
  data?: ShowMoreDetailsData | null;
};

const ShowMoreDetails = ({ data }: Props) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const state = location.state;
  const { universalStyles } = useTheme();

  const { data: dropdownData } = useQuery<GetDropdownsData, GetDropdownsVariables>(GET_DROPDOWNS, {
    client: getAuthApolloClient(),
    variables: { ddType: 'category' },
    errorPolicy: 'all',
  });

  const handleCategory = (categoryId: string) =>
    dropdownData?.getDropdowns?.dropdowns.find((item) => item.ddId === categoryId)?.ddLabel;

  useEffect(() => {
    if (data) {
      return;
    }
    if (location.state) {
      sessionStorage.setItem('seeMoreDetails', JSON.stringify(state));
    }
  }, [data, location.state, state]);

  const ShowMoreData = data ?? location.state ?? JSON.parse(sessionStorage.getItem('seeMoreDetails') || 'null');

  const genres: string[] = ShowMoreData?.item?.genre ? ShowMoreData?.item?.genre.split(',') : [];
  const rawTags = ShowMoreData?.item?.tags;
  let tags: string[] = [];
  if (typeof rawTags === 'string') {
    tags = rawTags.split(',');
  } else if (Array.isArray(rawTags)) {
    tags = rawTags;
  }

  const title = ShowMoreData?.item?.title || ShowMoreData?.item?.maTitle;
  const description = ShowMoreData?.item?.description || ShowMoreData?.item?.maDescription;
  const effectiveCategory = ShowMoreData?.item?.category || ShowMoreData?.item?.maCategory;

  const category = useMemo(() => {
    if (effectiveCategory) {
      return handleCategory(effectiveCategory);
    }
  }, [effectiveCategory]);

  return (
    <div>
      <h2 className={styles.heading} style={universalStyles.primaryTextColor}>
        {title}
      </h2>
      <div className={styles.primaryMetaDataContainer} dangerouslySetInnerHTML={{ __html: ShowMoreData?.primaryMetadata }} />
      {genres.length > 0 && (
        <div className={styles.genresContainer}>
          {genres &&
            genres?.length > 0 &&
            genres?.map((item, index) => (
              <div className={styles.genres} key={index} style={{ ...universalStyles?.genre, ...universalStyles.primaryTextColor }}>
                {item}
              </div>
            ))}
        </div>
      )}
      <div className={styles.descriptionContainer}>
        <span className={styles.description} style={universalStyles.primaryTextColor}>
          {description}
        </span>
      </div>
      {ShowMoreData?.item?.audio_language && (
        <div className={styles.detailsContainer}>
          <h3 className={styles.detailsHeader} style={universalStyles.primaryTextColor}>
            {t('show_more.audio_languages')}
          </h3>
          <p className={styles.detailsValue} style={universalStyles.secondaryText}>
            {ShowMoreData.item.audio_language}
          </p>
        </div>
      )}
      {ShowMoreData?.item?.sub_title && (
        <div className={styles.detailsContainer}>
          <h3 className={styles.detailsHeader} style={universalStyles.primaryTextColor}>
            {t('show_more.subtitles')}
          </h3>
          <p className={styles.detailsValue} style={universalStyles.secondaryText}>
            {ShowMoreData.item.sub_title}
          </p>
        </div>
      )}
      {ShowMoreData?.item?.cast && (
        <div className={styles.detailsContainer}>
          <h3 className={styles.detailsHeader} style={universalStyles.primaryTextColor}>
            {t('show_more.cast')}
          </h3>
          <p className={styles.detailsValue} style={universalStyles.secondaryText}>
            {ShowMoreData.item.cast}
          </p>
        </div>
      )}
      {ShowMoreData?.item?.director && (
        <div className={styles.detailsContainer}>
          <h3 className={styles.detailsHeader} style={universalStyles.primaryTextColor}>
            {t('show_more.director')}
          </h3>
          <p className={styles.detailsValue} style={universalStyles.secondaryText}>
            {ShowMoreData.item.director}
          </p>
        </div>
      )}
      {ShowMoreData?.item?.producer && (
        <div className={styles.detailsContainer}>
          <h3 className={styles.detailsHeader} style={universalStyles.primaryTextColor}>
            {t('show_more.producer')}
          </h3>
          <p className={styles.detailsValue} style={universalStyles.secondaryText}>
            {ShowMoreData.item.producer}
          </p>
        </div>
      )}
      {ShowMoreData?.item?.studio && (
        <div className={styles.detailsContainer}>
          <h3 className={styles.detailsHeader} style={universalStyles.primaryTextColor}>
            {t('show_more.studio')}
          </h3>
          <p className={styles.detailsValue} style={universalStyles.secondaryText}>
            {ShowMoreData.item.studio}
          </p>
        </div>
      )}
      {category && (
        <div className={styles.detailsContainer}>
          <h3 className={styles.detailsHeader} style={universalStyles.primaryTextColor}>
            {t('show_more.category', { defaultValue: 'Category' })}
          </h3>
          <div className={styles.categoryBadgeWrapper}>
            <span className={styles.categoryBadge}>{category}</span>
          </div>
        </div>
      )}
      {tags.length > 0 && (
        <div className={styles.detailsContainer}>
          <h3 className={styles.detailsHeader} style={universalStyles.primaryTextColor}>
            {t('show_more.tags', { defaultValue: 'Tags' })}
          </h3>
          <p className={styles.detailsValue} style={universalStyles.secondaryText}>
            {tags.map((tag) => (tag.trim().startsWith('#') ? tag.trim() : `#${tag.trim()}`)).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ShowMoreDetails;
